import '../i18n/autoTranslate';
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, FileText, Upload, Globe, Plus, Settings, CheckCircle, X, ChevronLeft, ChevronRight, AlertCircle ,Camera} from 'lucide-react';
import Header from '../../components/ui/Header';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert';
import AppBackground from '../../components/ui/AppBackground.tsx';


import SpotlightCard from '../../components/ui/SpotlightCard';
import Button from '../../components/ui/Button';
import StepProgressIndicator from '../../pages/interview-setup-configuration/components/StepProgressIndicator';
import Select from '../../components/ui/Select';
// import US from 'country-flag-icons/react/3x2/US';
// import DE from 'country-flag-icons/react/3x2/DE';
// import JP from 'country-flag-icons/react/3x2/JP';
// import BR from 'country-flag-icons/react/3x2/BR';
import TemplateSelection from './TemplateSelection';
import countries from '../../utils/countries';
import * as Flags from "country-flag-icons/react/3x2";
import Confetti from 'react-confetti'

import { getUserCountry } from '../../utils/getUserCountry';
import { resumeBuilderTour } from '../../utils/resumeTour';
import SEO from '../SEO';

//! Progress component
const Progress = React.forwardRef(({ className, value, ...props }, ref) => (
  <div
    ref={ref}
    className={`relative w-full overflow-hidden rounded-full bg-white/30 h-2 ${className || ''}`}
    {...props}
  >
    <div
      className="relative overflow-hidden bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] h-2 rounded-full transition-all duration-500 ease-smooth"
      style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }}
    >
      <div className="absolute -bottom-1 left-[20%] w-1/2 h-3 rounded-full bg-white/70 blur-[10px] pointer-events-none" />
    </div>
  </div>
));

//! Checkbox component
const Checkbox = React.forwardRef(({
  className,
  id,
  checked,
  disabled = false,
  required = false,
  label,
  description,
  error,
  onChange,
  ...props
}, ref) => {
  const checkboxId = id || `checkbox-${Math.random()?.toString(36)?.substr(2, 9)}`;

  return (
    <div className={`flex items-start space-x-3 ${className || ''}`}>
      <div className="relative flex items-center">
        <input
          type="checkbox"
          ref={ref}
          id={checkboxId}
          checked={checked}
          disabled={disabled}
          required={required}
          onChange={onChange}
          className="sr-only"
          {...props}
        />
        <label
          htmlFor={checkboxId}
          className={`
            w-4 h-4 rounded border-2 cursor-pointer transition-all duration-200 flex items-center justify-center
            ${checked 
              ? 'bg-indigo-600 border-indigo-600' 
              : 'bg-gray-800 border-gray-600 hover:border-gray-500'
            }
            ${disabled ? 'cursor-not-allowed opacity-50' : ''}
            ${error ? 'border-red-500' : ''}
          `}
        >
          {checked && (
            <CheckCircle className="h-3 w-3 text-white" />
          )}
        </label>
      </div>
      {(label || description || error) && (
        <div className="flex-1 space-y-1">
          {label && (
            <label
              htmlFor={checkboxId}
              className={`text-sm font-medium cursor-pointer ${
                error ? 'text-red-400' : 'text-white'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {label}
              {required && <span className="text-red-400 ml-1">*</span>}
            </label>
          )}
          {description && !error && (
            <p className="text-sm text-gray-400">{description}</p>
          )}
          {error && (
            <p className="text-sm text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
});


// API configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://ai-interview-back-node.onrender.com';


const requirements = {
  Germany: ['photo', 'birthDate'],
  Japan: ['birthDate'],
  'United States': [],
  Brazil: []
};

const inappropriateFields = {
  'United States': ['photo', 'birthDate'],
  Germany: [],
  Japan: [],
  Brazil: []
};

// Utility functions
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validatePhone = (phone) => /^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/\s/g, ''));

//! Custom RadioGroup component
const CustomRadioGroup = ({ selectedValue, onChange, options }) => (
  <div id="CustomRadioGroup" className="space-y-4 ">
    {options.map((option) => (
      <div 
        key={option.value} 
        className="relative"
      >
        <input
          type="radio"
          id={option.value}
          name="pathChoice"
          value={option.value}
          checked={selectedValue === option.value}
          onChange={(e) => onChange(e.target.value)}
          className="sr-only"
        />
        <button
          type="button"
          onClick={() => onChange(option.value)}
          className="w-full text-left"
        >
          <SpotlightCard
            className="custom-spotlight-card rounded-3xl overflow-hidden"
            spotlightColor={selectedValue === option.value ? "rgba(99, 102, 241, 0.26)" : "rgba(139, 92, 246, 0.10)"}
          >
            <div className={`
              relative rounded-3xl border transition-all duration-200 overflow-hidden
              bg-white/5
              ${selectedValue === option.value
                ? 'border-white/10 bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 shadow-[0_0_22px_rgba(99,102,241,0.22)]'
                : 'border-white/10 hover:border-purple-500/30 hover:shadow-[0_0_22px_rgba(99,102,241,0.12)]'
              }
            `}>
              <div className="absolute inset-0 opacity-30 pointer-events-none">
                <img
                  src={option.value === 'upload' ? "/professional-output-downloads.png" : "/from-scratch_v2j8c4.png"}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/35 to-black/70" />
              </div>
              <div className="absolute inset-0 opacity-25 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-black/0 via-black/25 to-black/55" />
              </div>
              <div className="relative p-6 flex items-start gap-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)] flex-shrink-0">
                  {option.value === 'upload' ? (
                    <Upload className="h-5 w-5 text-white" />
                  ) : (
                    <FileText className="h-5 w-5 text-white" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-lg font-semibold text-foreground">{option.label}</div>
                      <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
                        {option.description}
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border flex items-center justify-center ${
                      selectedValue === option.value ? 'border-white/20 bg-white/10' : 'border-white/10 bg-white/5'
                    }`}>
                      {selectedValue === option.value && (
                        <div className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-[#8B7CFF] to-[#6C63FF]" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SpotlightCard>
        </button>
      </div>
    ))}
  </div>
);

//! UploadResume component
const UploadResume = ({ onUpload, data,setAlertInfo }) => {
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  
  const handleFileChange = (e) => setFile(e.target.files[0]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    
    // Show loading alert
    setAlertInfo({
      variant: "default",
      title: "Processing Resume",
      description: "Extracting information from your PDF...",
      show: true
    });
  
    try {
      const formData = new FormData();
      formData.append('resume', file);
  
      const response = await fetch(`${API_BASE_URL}/extract-text`, {
        method: 'POST',
        body: formData
      });
  
      const result = await response.json();
  
      if (result.success) {
        // Map the extracted data to your resumeData structure
        const extractedData = result.extractedData;
        const extractedPersonalInfo = extractedData?.personalInfo || {};
        const fullName = String(extractedPersonalInfo?.name || "").trim();
        const [firstNameFromFull, ...restName] = fullName ? fullName.split(/\s+/) : [];
        const lastNameFromFull = restName.join(" ").trim();
        const firstName = extractedPersonalInfo?.firstName || firstNameFromFull || "";
        const lastName = extractedPersonalInfo?.lastName || lastNameFromFull || "";

        const hasMeaningfulData =
          Boolean(String(extractedData?.objectif || '').trim()) ||
          Boolean(String(firstName).trim()) ||
          Boolean(String(lastName).trim()) ||
          Boolean(String(extractedPersonalInfo?.email || '').trim()) ||
          Boolean(String(extractedPersonalInfo?.phone || '').trim()) ||
          Boolean(String(extractedPersonalInfo?.address || '').trim()) ||
          (Array.isArray(extractedData?.workExperience) && extractedData.workExperience.length > 0) ||
          (Array.isArray(extractedData?.education) && extractedData.education.length > 0) ||
          (Array.isArray(extractedData?.skills) && extractedData.skills.length > 0);

        if (!hasMeaningfulData) {
          setExtracted(false);
          setAlertInfo({
            variant: "destructive",
            title: "Extraction Returned Empty Data",
            description: "We couldn't extract usable information from this file. Try another PDF or continue by filling the form manually.",
            show: true
          });
          return;
        }
        
        onUpload(prev => ({
          ...prev,
          objectif: extractedData?.objectif || prev.objectif || '',
          personalInfo: {
            ...prev.personalInfo,
            ...extractedPersonalInfo,
            firstName,
            lastName,
          },
          workExperience: extractedData.workExperience || [],
          education: extractedData.education || [],
          skills: extractedData.skills || [],
          optional: prev.optional,
          hasExtractedResume: true
        }));
        
        setExtracted(true);
        // Show success alert
        setAlertInfo({
          variant: "default",
          title: "Success!",
          description: "Resume information extracted successfully!",
          show: true
        });
      } else {
        // Fallback to manual extraction
        console.error('Extraction failed:', result.error);
        
        setAlertInfo({
          variant: "destructive",
          title: "Extraction Failed",
          description: "Failed to extract resume data. Please fill in the information manually.",
          show: true
        });
      }
    } catch (error) {
      console.error('Error uploading resume:', error);
      
      // Fallback to mock data if API fails
      const mockExtractedData = {
        personalInfo: {
          firstName: 'John',
          lastName: 'Doe',
          email: 'john.doe@example.com',
          phone: '+1-555-0123',
          address: '123 Main St, City, State 12345'
        },
        workExperience: [{
          id: Date.now(),
          title: 'Software Engineer',
          company: 'Tech Company',
          description: 'Developed web applications using React and Node.js',
          startDate: '2020-01',
          endDate: '2023-12'
        }],
        education: [{
          id: Date.now() + 1,
          degree: 'Bachelor of Computer Science',
          institution: 'University of Technology',
          startDate: '2016-09',
          endDate: '2020-06'
        }],
        skills: ['JavaScript', 'React', 'Node.js', 'Python']
      };
  
      onUpload(prev => ({
        ...prev,
        ...mockExtractedData,
        optional: prev.optional,
        hasExtractedResume: true
      }));
  
      setAlertInfo({
        variant: "destructive",
        title: "Connection Error",
        description: "Using sample data. Please review and edit the information.",
        show: true
      });
    } finally {
      setLoading(false);
      // Auto-hide alert after 5 seconds
      setTimeout(() => setAlertInfo(prev => ({ ...prev, show: false })), 5000);
    }
  };

  return (
    <div id="UploadResume" className="space-y-6">
    <SEO
  title="Resume AI — Improve Your Resume for Better Interview Results"
  description="Use InterviewGenieAI Resume AI to analyze your CV, improve weak sections, prepare better answers, and increase your chances of getting hired."
  canonical="https://interviewgenieai.com/resume-ai"
/>
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-3 mb-1">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
            <Upload className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Upload Resume</h2>
        </div>
        <p className="text-muted-foreground">Extract your data and refine it in a structured flow.</p>
      </div>
      
      <div
        className={`
          relative border border-dashed rounded-3xl p-8 text-center transition-all duration-200 bg-white/5
          ${dragActive 
            ? 'border-white/30 bg-white/10' 
            : 'border-white/15 hover:border-white/25'
          }
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="mx-auto mb-4 w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
          <Upload className="h-6 w-6 text-white/70" />
        </div>
        <div className="space-y-2">
          <p className="text-lg font-medium text-white">
            Drop your resume here, or{' '}
            <label htmlFor="file-upload" className="text-white underline decoration-white/20 cursor-pointer hover:decoration-white/60">
              browse
            </label>
          </p>
          <p className="text-sm text-white/55">Supports PDF and DOCX files</p>
        </div>
        <input
          id="file-upload"
          type="file"
          accept=".pdf,.docx"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {file && (
        <div className="bg-white/5 border border-white/10 rounded-3xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                <FileText className="h-5 w-5 text-white/70" />
              </div>
              <span className="text-white font-medium">{file.name}</span>
            </div>
            <button
              onClick={() => setFile(null)}
              className="text-white/60 hover:text-white transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <button
        onClick={handleUpload}
        disabled={!file || loading}
        className="w-full px-6 py-3 rounded-2xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold hover:from-[#5B55FF] hover:to-[#7F72FF] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Processing...</span>
          </div>
        ) : (
          'Upload and Extract Data'
        )}
      </button>
      {
        extracted && (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-6">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-emerald-500/20 border border-emerald-400/20 rounded-2xl flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="text-green-400 font-semibold text-lg mb-2">
                  Information Successfully Extracted
                </h4>
                <p className="text-white/70 text-sm mb-3">
                  Your resume data has been processed and is ready for refinement. Choose your preferred editing approach:
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start space-x-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-white/10 text-white rounded-full text-xs font-medium mt-0.5 flex-shrink-0 border border-white/10">
                      1
                    </span>
                    <span className="text-white">
                      <strong>Direct Document Edit:</strong> Download the DOCX file and modify the content directly in the document
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-white/10 text-white rounded-full text-xs font-medium mt-0.5 flex-shrink-0 border border-white/10">
                      2
                    </span>
                    <span className="text-white">
                      <strong>Form-Based Refinement:</strong> Return to the beginning and select "Start from Scratch" - your extracted data will be pre-filled in the forms for easy modification
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-white/10 text-white rounded-full text-xs font-medium mt-0.5 flex-shrink-0 border border-white/10">
                      3
                    </span>
                    <span className="text-white">
                      <strong>Continue Enhancement:</strong> Proceed from this point to add additional sections or refine existing content
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-white/65 text-xs font-medium">
                    All extracted information is preserved. Choosing "Start from Scratch" will pre-populate the forms with your data for streamlined editing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

//! FormBuilder component
const FormBuilder = ({ data, onChange, setAlertInfo }) => {
  const [errors, setErrors] = useState({});
  const [skillsInput, setSkillsInput] = useState(''); 
  const inputRef = useRef(null);
  

  
  const validatePersonalInfo = () => {
    const newErrors = {};
    if (!data.personalInfo.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!data.personalInfo.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!data.personalInfo.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(data.personalInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!data.personalInfo.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(data.personalInfo.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!data.personalInfo.address?.trim()) newErrors.address = 'Address is required';
    return newErrors;
  };

  const showAlert = (variant, title, description) => {
    setAlertInfo({
      show: true,
      variant,
      title,
      description
    });
    
    // Auto-hide alert after 5 seconds
    setTimeout(() => {
      setAlertInfo(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const handlePersonalChange = (field, value) => {
    onChange(prev => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, [field]: value }
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleWorkChange = (index, field, value) => {
    const updatedWork = [...data.workExperience];
    updatedWork[index] = { ...updatedWork[index], [field]: value };
    onChange(prev => ({ ...prev, workExperience: updatedWork }));
  };

  const addWorkExperience = () => {
    onChange(prev => ({
      ...prev,
      workExperience: [...prev.workExperience, { 
        id: Date.now(), 
        title: '', 
        company: '', 
        description: '', 
        startDate: '', 
        endDate: '' 
      }]
    }));
  };

  const removeWorkExperience = (index) => {
    if (data.workExperience.length <= 1) {
      showAlert("destructive", "Cannot Remove", "You need to have at least one work experience entry.");
      return;
    }
    
    onChange(prev => ({
      ...prev,
      workExperience: prev.workExperience.filter((_, i) => i !== index)
    }));
    
    showAlert("default", "Removed", "Work experience entry has been removed.");
  };

  const handleEducationChange = (index, field, value) => {
    const updatedEducation = [...data.education];
    updatedEducation[index] = { ...updatedEducation[index], [field]: value };
    onChange(prev => ({ ...prev, education: updatedEducation }));
  };

  const addEducation = () => {
    onChange(prev => ({
      ...prev,
      education: [...prev.education, { 
        id: Date.now(), 
        degree: '', 
        institution: '', 
        startDate: '', 
        endDate: '' 
      }]
    }));
  };

  const removeEducation = (index) => {
    if (data.education.length <= 1) {
      showAlert("destructive", "Cannot Remove", "You need to have at least one education entry.");
      return;
    }
    
    onChange(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
    
    showAlert("default", "Removed", "Education entry has been removed.");
  };

  const handleSkillsInputChange = (value) => {
    setSkillsInput(value);
  };
  
  const handleSkillsKeyDown = (e) => {
    if (e.key === ',') {
      e.preventDefault();
      e.stopPropagation(); // Also stop event propagation
      
      const newSkill = skillsInput.trim();
      if (newSkill.length > 0) {
        const updatedSkills = [...data.skills, newSkill];
        onChange(prev => ({
          ...prev,
          skills: updatedSkills,
        }));
        setSkillsInput('');
      }
      return false; // Additional prevention
    }
  };
  
  const handleSkillsBlur = () => {
    // Add current input as a skill when field loses focus
    const newSkill = skillsInput.trim();
    if (newSkill.length > 0) {
      const updatedSkills = [...data.skills, newSkill];
      onChange(prev => ({
        ...prev,
        skills: updatedSkills,
      }));
      
      // Clear input ONLY if we added a skill
      setSkillsInput('');
    }
  };
  

  const removeSkill = (indexToRemove) => {
    const updatedSkills = data.skills.filter((_, index) => index !== indexToRemove);
    onChange(prev => ({
      ...prev,
      skills: updatedSkills,
    }));
  };
  

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    // Basic phone validation - allows various formats
    const re = /^[\+]?[1-9][\d]{0,15}$/;
    return re.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  useEffect(() => {
    const newErrors = validatePersonalInfo();
    setErrors(newErrors);
    
    // Show error alert if there are validation errors
    if (Object.keys(newErrors).length > 0) {
      showAlert("destructive", "Validation Error", "Please check the highlighted fields.");
    }
  }, [data.personalInfo]);

  return (
    <div id="FormBuilder" className="space-y-8">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-3 mb-1">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
            <Settings className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Build Resume</h2>
        </div>
        <p className="text-muted-foreground">Fill in your information to create a professional resume</p>
      </div>

      {/* Personal Information */}
      <section className="space-y-6">
        <h3 className="text-xl font-semibold text-white border-b border-white/10 pb-2">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              First name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter your full name"
              value={data.personalInfo.firstName || ''}
              onChange={(e) => handlePersonalChange('firstName', e.target.value)}
              className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition ${
                errors.firstName ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.firstName && <p className="mt-1 text-sm text-red-400">{errors.firstName}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Last name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="Enter your full name"
              value={data.personalInfo.lastName || ''}
              onChange={(e) => handlePersonalChange('lastName', e.target.value)}
              className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition ${
                errors.lastName ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.lastName && <p className="mt-1 text-sm text-red-400">{errors.lastName}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              placeholder="your.email@example.com"
              value={data.personalInfo.email || ''}
              onChange={(e) => handlePersonalChange('email', e.target.value)}
              className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition ${
                errors.email ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Phone <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              placeholder="+1-555-0123"
              value={data.personalInfo.phone || ''}
              onChange={(e) => handlePersonalChange('phone', e.target.value)}
              className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition ${
                errors.phone ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.phone && <p className="mt-1 text-sm text-red-400">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Address <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              placeholder="123 Main St, City, State"
              value={data.personalInfo.address || ''}
              onChange={(e) => handlePersonalChange('address', e.target.value)}
              className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition ${
                errors.address ? 'border-red-500' : 'border-white/10'
              }`}
            />
            {errors.address && <p className="mt-1 text-sm text-red-400">{errors.address}</p>}
          </div>
        </div>
      </section>

      {/* Work Experience */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white border-b border-white/10 pb-2">Work Experience</h3>
          <button
            onClick={addWorkExperience}
            className="flex items-center space-x-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold hover:from-[#5B55FF] hover:to-[#7F72FF] transition"
          >
            <Plus className="h-4 w-4" />
            <span>Add Experience</span>
          </button>
        </div>
        
        {data.workExperience.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-white/60 mb-4">No work experience added yet</p>
            <button
              onClick={addWorkExperience}
              className="px-6 py-2 rounded-2xl bg-white/10 border border-white/10 text-white hover:bg-white/15 transition"
            >
              Add Your First Job
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {data.workExperience.map((exp, index) => (
              <div key={exp.id || index} className="bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-white">Experience #{index + 1}</h4>
                  <button
                    onClick={() => removeWorkExperience(index)}
                    className="text-red-400 hover:text-red-300 transition"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    placeholder="Job Title"
                    value={exp.title || ''}
                    onChange={(e) => handleWorkChange(index, 'title', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                  <input
                    placeholder="Company"
                    value={exp.company || ''}
                    onChange={(e) => handleWorkChange(index, 'company', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                  <input
                    type="date"
                    value={exp.startDate || ''}
                    onChange={(e) => handleWorkChange(index, 'startDate', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                  <input
                    type="date"
                    value={exp.endDate || ''}
                    onChange={(e) => handleWorkChange(index, 'endDate', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                </div>
                <textarea
                  placeholder="Job Description"
                  value={exp.description || ''}
                  onChange={(e) => handleWorkChange(index, 'description', e.target.value)}
                  rows="4"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Education */}
      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-white border-b border-white/10 pb-2">Education</h3>
          <button
            onClick={addEducation}
            className="flex items-center space-x-2 px-4 py-2 rounded-2xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold hover:from-[#5B55FF] hover:to-[#7F72FF] transition"
          >
            <Plus className="h-4 w-4" />
            <span>Add Education</span>
          </button>
        </div>
        
        {data.education.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-white/60 mb-4">No education added yet</p>
            <button
              onClick={addEducation}
              className="px-6 py-2 rounded-2xl bg-white/10 border border-white/10 text-white hover:bg-white/15 transition"
            >
              Add Your Education
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {data.education.map((edu, index) => (
              <div key={edu.id || index} className="bg-white/5 border border-white/10 rounded-3xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-medium text-white">Education #{index + 1}</h4>
                  <button
                    onClick={() => removeEducation(index)}
                    className="text-red-400 hover:text-red-300 transition"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    placeholder="Degree"
                    value={edu.degree || ''}
                    onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                  <input
                    placeholder="Institution"
                    value={edu.institution || ''}
                    onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                  <input
                    type="date"
                    value={edu.startDate || ''}
                    onChange={(e) => handleEducationChange(index, 'startDate', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                  <input
                    type="date"
                    value={edu.endDate || ''}
                    onChange={(e) => handleEducationChange(index, 'endDate', e.target.value)}
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Skills */}
      <section className="space-y-4">
        <h3 className="text-xl font-semibold text-white border-b border-white/10 pb-2">Skills</h3>
        <div>
          <label className="block text-sm font-medium text-white/70 mb-2">
            Skills (press comma to add each skill)
          </label>
          <input
            // ref={inputRef}
            placeholder="Type a skill and press comma to add it"
            value={skillsInput}
            onChange={(e) => handleSkillsInputChange(e.target.value)}
            onKeyDown={handleSkillsKeyDown}
            onBlur={handleSkillsBlur}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-2xl text-white placeholder:text-white/40 focus:ring-2 focus:ring-white/10 focus:border-transparent transition"
          />
          {data.skills.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {data.skills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white/5 text-white/80 rounded-full text-sm border border-white/10 flex items-center gap-1"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeSkill(index)}
                    className="text-red-400 hover:text-red-300 transition"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

//! CountrySelection component
const CountrySelection = ({ selectedCountry, onSelect }) => {
  const [selectedCountryObj, setSelectedCountryObj] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState(null);

  // Update selectedCountryObj when selectedCountry changes
  useEffect(() => {
    const countryObj = countries.find(country => country.value === selectedCountry);
    setSelectedCountryObj(countryObj || null);
  }, [selectedCountry]);

  // Auto-detect country on component mount
  useEffect(() => {
    const detectCountry = async () => {
      const countryCode = await getUserCountry();
      const countryObj = countries.find(v => v.value === countryCode);
      
      if (countryObj) {
        setDetectedCountry(countryObj);
        // Optional: auto-select detected country
        // onSelect(countryCode);
      }
    };
    
    detectCountry();
  }, []);

  const handleUseDetectedCountry = () => {
    if (detectedCountry) {
      onSelect(detectedCountry.value);
    }
  };

  return (
    <div id="CountrySelection" className="space-y-6 min-h-96">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-white">Select Target Country</h2>
        <p className="text-gray-400">Choose the country where you'll be applying for jobs</p>
      </div>
      
      {/* Auto-suggestion section */}
      {detectedCountry && detectedCountry.value !== selectedCountry && (
        <div className="text-center space-y-2">
          <h2 className="text-xl font-bold text-white">Auto-suggestion</h2>
          <p className="text-gray-400">
            We detected {detectedCountry.label} as your country - 
            <button 
              onClick={handleUseDetectedCountry}
              className="ml-1 text-indigo-400 hover:text-indigo-300 underline"
            >
              use this instead
            </button>
          </p>
        </div>
      )}
      
      <div className="max-w-md mx-auto">
        <Select
          label="Country"
          required
          options={countries}
          value={selectedCountry}
          onChange={onSelect}
          placeholder="Select country"
          className="w-full"
          searchable
        />
      </div>

      {selectedCountry && selectedCountryObj && (
        <div className="bg-indigo-600/10 border border-indigo-500/30 rounded-lg p-4 max-w-md mx-auto">
          <div className="flex items-center space-x-2 mb-2">
            <Globe className="h-5 w-5 text-indigo-400" />
            <h3 className="font-semibold text-indigo-300">
              Country Selected: {selectedCountryObj.label}
            </h3>
          </div>
        </div>
      )}
    </div>
  );
};


//! AdditionalFields component
const AdditionalFields = ({ country, onChange, data }) => {
  // Get the country label from the country value
  const getCountryLabel = (countryValue) => {
    const countryObj = countries.find(c => c.value === countryValue);
    return countryObj ? countryObj.label : countryValue;
  };
  
  
  const countryLabel = getCountryLabel(country);
  const reqFields = requirements[countryLabel] || [];

  const handleInputChange = (field, value) => {
    onChange(prev => ({
      ...prev,
      optional: { ...prev.optional, [field]: value }
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        // Store the image as a base64 string
        handleInputChange('photo', event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (reqFields.length === 0) {
    return (
      <div id="AdditionalFields" className="text-center space-y-4">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-white">Additional Information</h2>
          <p className="text-gray-400">Country-specific requirements for {countryLabel}</p>
        </div>
        <div className="bg-green-600/10 border border-green-500/30 rounded-lg p-6">
          <CheckCircle className="mx-auto h-12 w-12 text-green-400 mb-4" />
          <p className="text-green-300 text-lg">Great! No additional information is required for {countryLabel}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-white">Additional Information for {countryLabel}</h2>
        <p className="text-gray-400">Please provide the following required information</p>
      </div>
      
      <div className="space-y-4 max-w-md mx-auto">
        {reqFields.includes('photo') && (
          <div className="bg-yellow-600/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex flex-col items-center mb-4">
              {data.optional?.photo ? (
                <img 
                  src={data.optional.photo} 
                  alt="Professional" 
                  className="rounded-full w-20 h-20 object-cover border-2 border-yellow-500"
                />
              ) : (
                <div className="rounded-full w-20 h-20 bg-gray-700 flex items-center justify-center border-2 border-dashed border-yellow-500">
                  <Camera className="h-8 w-8 text-gray-400" />
                </div>
              )}
            </div>
            
            <label className="block text-sm font-medium text-yellow-300 mb-2">
              Professional Photo <span className="text-red-400">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
            />
            <p className="text-xs text-gray-400 mt-1">Required for resumes in {countryLabel}</p>
          </div>
        )}
        
        {reqFields.includes('birthDate') && (
          <div className="bg-yellow-600/10 border border-yellow-500/30 rounded-lg p-4">
            <label className="block text-sm font-medium text-yellow-300 mb-2">
              Date of Birth <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              value={data.optional?.birthDate || ''}
              onChange={(e) => handleInputChange('birthDate', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
            />
            <p className="text-xs text-gray-400 mt-1">Required for resumes in {countryLabel}</p>
          </div>
        )}
      </div>
    </div>
  );
};


//! SmartAdjustment component
const SmartAdjustment = ({ country, onChange, data }) => {
  // Get the country label from the country value
  const getCountryLabel = (countryValue) => {
    const countryObj = countries.find(c => c.value === countryValue);
    return countryObj ? countryObj.label : countryValue;
  };
  
  const countryLabel = getCountryLabel(country);
  const reqFields = requirements[countryLabel] || [];
  const hideFields = inappropriateFields[countryLabel] || [];
  const [checkedFields, setCheckedFields] = useState({});

  const handleOptionalChange = (field, value) => {
    onChange(prev => ({
      ...prev,
      optional: { ...prev.optional, [field]: value }
    }));
  };

  const handleFieldCheck = (field, checked) => {
    setCheckedFields(prev => ({ ...prev, [field]: checked }));
  };

  return (
    <div id="SmartAdjustment" className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-white">Smart Form Adjustment</h2>
        <p className="text-gray-400">Optimizing your resume for {countryLabel} standards</p>
      </div>

      {hideFields.length > 0 && (
        <div className="bg-red-600/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <h3 className="font-semibold text-red-300">Fields Hidden for {countryLabel}</h3>
          </div>
          <p className="text-sm text-gray-300 mb-3">
            These fields are considered inappropriate for resumes in {countryLabel}:
          </p>
          <div className="space-y-2">
            {hideFields.map(field => (
              <Checkbox
                key={field}
                checked={true}
                disabled={true}
                label={`${field.charAt(0).toUpperCase() + field.slice(1)} (Hidden)`}
                description="This field will not appear in your final resume"
              />
            ))}
          </div>
        </div>
      )}

      {reqFields.length > 0 ? (
        <div className="space-y-4">
          <div className="bg-green-600/10 border border-green-500/30 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-400" />
              <h3 className="font-semibold text-green-300">Required Fields for {countryLabel}</h3>
            </div>
            <p className="text-sm text-gray-300 mb-3">
              Please complete the following required fields:
            </p>
          </div>

          <div className="space-y-4 max-w-md mx-auto">
            {reqFields.map(field => (
              <div key={field} className="bg-yellow-600/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="space-y-3">
                  <Checkbox
                    checked={checkedFields[field] || false}
                    onChange={(e) => handleFieldCheck(field, e.target.checked)}
                    label={`${field.charAt(0).toUpperCase() + field.slice(1)} completed`}
                    description={`This field is required for ${countryLabel} resumes`}
                  />
                  
                  {field === 'photo' && (
                    <div>
                      <label className="block text-sm font-medium text-yellow-300 mb-2">
                        Professional Photo
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          handleOptionalChange('photo', e.target.files[0]);
                          handleFieldCheck('photo', !!e.target.files[0]);
                        }}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      />
                    </div>
                  )}
                  
                  {field === 'birthDate' && (
                    <div>
                      <label className="block text-sm font-medium text-yellow-300 mb-2">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={data.optional?.birthDate || ''}
                        onChange={(e) => {
                          handleOptionalChange('birthDate', e.target.value);
                          handleFieldCheck('birthDate', !!e.target.value);
                        }}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-green-600/10 border border-green-500/30 rounded-lg p-6 text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-green-400 mb-4" />
          <p className="text-green-300 text-lg">Perfect! No adjustments needed for {countryLabel}.</p>
        </div>
      )}
    </div>
  );
};

//! ReviewGenerate component
const ReviewGenerate = ({ data, setAlertInfo,setShowConfetti }) => {
  const [loading, setLoading] = useState(false);
  const [showFullData, setShowFullData] = useState(true);
  const [generatedFile, setGeneratedFile] = useState(null); // Track generated file info
  
  const handleGenerate = async () => {
    setLoading(true);
    setGeneratedFile(null); // Reset previous file
    
    // Show generating alert
    setAlertInfo({
      variant: "default",
      title: "Generating Resume",
      description: "Creating your customized resume with filled placeholders...",
      show: true
    });
  
    try {
      console.log('=== Starting Resume Generation ===');
      console.log('Resume Data:', data);
      console.log('Selected Template:', data.selectedTemplate);

      // Call the new fill-resume-template endpoint
      const response = await fetch(`${API_BASE_URL}/api/fill-resume-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resumeData: data,
          templateId: data.selectedTemplate?.id
        }),
      });

      const result = await response.json();
      console.log('Response:', result);

      if (result.success) {
        const absoluteDownloadUrl = /^https?:\/\//i.test(result.downloadUrl)
          ? result.downloadUrl
          : `${API_BASE_URL}${result.downloadUrl.startsWith('/') ? '' : '/'}${result.downloadUrl}`;

        const fileInfo = {
          downloadUrl: absoluteDownloadUrl,
          filename: result.filename || `Resume_${data.personalInfo.firstName.replace(/\s+/g, '_')}.docx`
        };
        setGeneratedFile(fileInfo);
        // Download the generated file
        const downloadUrl = absoluteDownloadUrl;
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.download = `Resume_${data.personalInfo.firstName.replace(/\s+/g, '_')}.docx`;
        downloadLink.target = '_blank';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Show success alert
        setAlertInfo({
          variant: "default",
          title: "Success!",
          description: `Resume generated successfully using ${data.selectedTemplate?.name} template!`,
          show: true
        });
        
        setShowConfetti(true);
        setTimeout(() => {
          setShowConfetti(false);
        }, 6000);
      } else {
        throw new Error(result.error || 'Failed to generate resume');
      }
      
    } catch (error) {
      console.error('Error generating resume:', error);
      
      setAlertInfo({
        variant: "destructive",
        title: "Generation Failed",
        description: `Failed to generate resume: ${error.message}`,
        show: true
      });
    } finally {
      setLoading(false);
      // Auto-hide alert after 5 seconds
      setTimeout(() => setAlertInfo(prev => ({ ...prev, show: false })), 5000);
    }
  };

  const getSummaryStats = () => {
    return {
      personalInfo: Object.keys(data.personalInfo).filter(key => data.personalInfo[key]).length,
      workExperience: data.workExperience.length,
      education: data.education.length,
      skills: data.skills.length,
      optional: Object.keys(data.optional).filter(key => data.optional[key]).length
    };
  };

  const stats = getSummaryStats();
  const completionPercentage = Math.round(
    ((stats.personalInfo > 0 ? 20 : 0) + 
     (stats.workExperience > 0 ? 20 : 0) + 
     (stats.education > 0 ? 20 : 0) + 
     (stats.skills > 0 ? 20 : 0) +
     (data.selectedTemplate ? 20 : 0))
  );

  // DownloadInstructions component - moved outside the main return
  const DownloadInstructions = ({ downloadUrl, filename }) => {
    if (!downloadUrl) return null;
    
    return (
      <SpotlightCard className="custom-spotlight-card rounded-3xl overflow-hidden" spotlightColor="rgba(99, 102, 241, 0.12)">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-2xl bg-white/5 border border-white/10">
                <FileText className="h-5 w-5 text-white/80" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Download Instructions</h3>
                <p className="text-sm text-white/60">DOCX is editable; export to PDF when ready.</p>
              </div>
            </div>
            <Button asChild iconName="ArrowRight" iconPosition="right">
              <a href={downloadUrl} download={filename}>
                Download DOCX
              </a>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/40 p-4">
              <div className="text-xs text-white/50 font-semibold mb-2">STEP 1</div>
              <div className="text-white font-semibold mb-1">Download the DOCX</div>
              <div className="text-sm text-white/65">Save the file locally so you can edit it.</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/40 p-4">
              <div className="text-xs text-white/50 font-semibold mb-2">STEP 2</div>
              <div className="text-white font-semibold mb-1">Edit & customize</div>
              <div className="text-sm text-white/65">Use Word, Google Docs, or LibreOffice to refine formatting and content.</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/40 p-4">
              <div className="text-xs text-white/50 font-semibold mb-2">STEP 3</div>
              <div className="text-white font-semibold mb-1">Export to PDF</div>
              <div className="text-sm text-white/65">Export or download as PDF for final sharing.</div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-white/70">
              <div className="space-y-2">
                <div className="text-white font-semibold">Microsoft Word</div>
                <div>File → Save As → PDF (*.pdf)</div>
              </div>
              <div className="space-y-2">
                <div className="text-white font-semibold">Google Docs</div>
                <div>File → Download → PDF Document (.pdf)</div>
              </div>
              <div className="space-y-2">
                <div className="text-white font-semibold">LibreOffice Writer</div>
                <div>File → Export As → Export as PDF</div>
              </div>
              <div className="space-y-2">
                <div className="text-white font-semibold">Apple Pages</div>
                <div>File → Export To → PDF</div>
              </div>
            </div>
          </div>
        </div>
      </SpotlightCard>
    );
  };

  return (
    <div id="ReviewGenerate" className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-3 mb-1">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
            <CheckCircle className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-foreground">Review & Generate Resume</h2>
        </div>
        <p className="text-muted-foreground">Review your information and selected template before generating.</p>
      </div>

      <SpotlightCard className="custom-spotlight-card rounded-3xl overflow-hidden" spotlightColor="rgba(99, 102, 241, 0.18)">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-2xl bg-white/5 border border-white/10">
                <FileText className="h-4 w-4 text-white/80" />
              </div>
              <h3 className="text-lg font-semibold text-white">Resume Completion</h3>
            </div>
            <span className="text-2xl font-bold text-indigo-300">{completionPercentage}%</span>
          </div>
          <Progress value={completionPercentage} className="mb-4" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <CheckCircle className={`h-4 w-4 ${stats.personalInfo > 0 ? 'text-emerald-400' : 'text-white/25'}`} />
              <span className="text-white/70">Personal ({stats.personalInfo}/4)</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className={`h-4 w-4 ${stats.workExperience > 0 ? 'text-emerald-400' : 'text-white/25'}`} />
              <span className="text-white/70">Work ({stats.workExperience})</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className={`h-4 w-4 ${stats.education > 0 ? 'text-emerald-400' : 'text-white/25'}`} />
              <span className="text-white/70">Education ({stats.education})</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className={`h-4 w-4 ${stats.skills > 0 ? 'text-emerald-400' : 'text-white/25'}`} />
              <span className="text-white/70">Skills ({stats.skills})</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className={`h-4 w-4 ${data.selectedTemplate ? 'text-emerald-400' : 'text-white/25'}`} />
              <span className="text-white/70">Template</span>
            </div>
          </div>
        </div>
      </SpotlightCard>

      {/* Selected Template Display */}
      {data.selectedTemplate && (
        <SpotlightCard className="custom-spotlight-card rounded-3xl overflow-hidden" spotlightColor="rgba(139, 92, 246, 0.12)">
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Selected Template</h3>
              <span className="text-xs text-white/60">PDF preview</span>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-32 h-40 bg-[#0B0B12] rounded-2xl overflow-hidden flex-shrink-0 border border-white/10">
              <object
                data={`${data.selectedTemplate.previewUrl}#page=1&view=FitH&zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`}
                type="application/pdf"
                className="w-full h-full pointer-events-none"
              >
                <embed
                  src={`${data.selectedTemplate.previewUrl}#page=1&view=FitH&zoom=page-fit&toolbar=0&navpanes=0&scrollbar=0`}
                  type="application/pdf"
                  className="w-full h-full pointer-events-none"
                />
              </object>
            </div>

            <div className="flex-1">
              <h4 className="font-semibold text-white text-lg">{data.selectedTemplate.name}</h4>
              <p className="text-indigo-300 text-sm mb-2">{data.selectedTemplate.category}</p>
              <p className="text-white/70 text-sm mb-3">{data.selectedTemplate.description}</p>
              <div className="flex flex-wrap gap-1">
                {data.selectedTemplate.tags.map((tag, index) => (
                  <span key={index} className="px-2 py-1 bg-white/5 border border-white/10 text-xs text-white/70 rounded-xl">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
          </div>
        </SpotlightCard>
      )}

      {/* Resume Summary */}
      <SpotlightCard className="custom-spotlight-card rounded-3xl overflow-hidden" spotlightColor="rgba(99, 102, 241, 0.12)">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Resume Summary</h3>
            <button
              onClick={() => setShowFullData(!showFullData)}
              className="px-3 py-2 rounded-2xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 hover:text-white transition text-sm font-semibold"
            >
              {showFullData ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
      
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-indigo-300" />
              <span className="text-white font-medium">Target Country: {data.country || 'Not selected'}</span>
            </div>
          
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-indigo-300" />
              <span className="text-white font-medium">
                Full name: {data.personalInfo.firstName || 'Not provided'} {data.personalInfo.lastName || 'Not provided'}<br/>
              </span>
            </div>
      
            {showFullData && (
              <div className="rounded-2xl border border-white/10 bg-[#0B0B12]/40 p-4 h-full overflow-hidden">
                <div className="space-y-6 text-sm text-white/75">
                  <div>
                    <h4 className="text-indigo-300 font-semibold mb-2">Objective</h4>
                    <span className="text-white">{data.objectif || data.personalInfo.objectif || 'Not provided'}</span>
                  </div>
                
                {/* Personal Information */}
                <div>
                  <h4 className="text-indigo-300 font-semibold mb-2">Personal Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                    <div className="flex flex-col">
                      <span className="text-white/50">Email:</span>
                      <span className="text-white">{data.personalInfo.email || 'Not provided'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-white/50">Phone:</span>
                      <span className="text-white">{data.personalInfo.phone || 'Not provided'}</span>
                    </div>
                    <div className="flex flex-col md:col-span-2">
                      <span className="text-white/50">Address:</span>
                      <span className="text-white">{data.personalInfo.address || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
      
                {/* Work Experience */}
                <div>
                  <h4 className="text-indigo-300 font-semibold mb-2">Work Experience</h4>
                  <div className="space-y-4 ml-4">
                    {data.workExperience.map((work, index) => (
                      <div key={index} className="border-l-2 border-indigo-500 pl-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="text-white font-medium">{work.title}</h5>
                            <p className="text-white/55">{work.company}</p>
                          </div>
                          <span className="text-white/60 text-xs bg-white/5 border border-white/10 px-2 py-1 rounded-xl">
                            {work.startDate} - {work.endDate}
                          </span>
                        </div>
                        {work.description && (
                          <div className="mt-2 text-white/70 text-sm">
                            {work.description.split('\n').map((line, i) => (
                              <p key={i} className={line.startsWith('•') ? 'ml-2' : ''}>
                                {line}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
      
                {/* Education */}
                <div>
                  <h4 className="text-indigo-300 font-semibold mb-2">Education</h4>
                  <div className="space-y-3 ml-4">
                    {data.education.map((edu, index) => (
                      <div key={index} className="border-l-2 border-green-500 pl-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="text-white font-medium">{edu.degree}</h5>
                            <p className="text-white/55">{edu.institution}</p>
                          </div>
                          {(edu.startDate || edu.endDate) && (
                            <span className="text-white/60 text-xs bg-white/5 border border-white/10 px-2 py-1 rounded-xl">
                              {edu.startDate || ''} {edu.startDate && edu.endDate ? ' - ' : ''} {edu.endDate || ''}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
      
                {/* Skills */}
                <div>
                  <h4 className="text-indigo-300 font-semibold mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2 ml-4">
                    {data.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="bg-white/5 text-white/75 px-3 py-1 rounded-full text-sm border border-white/10"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
      
                {/* Template Information */}
                <div>
                  <h4 className="text-indigo-300 font-semibold mb-2">Selected Template</h4>
                  <div className="ml-4">
                    <div className="flex flex-col">
                      <span className="text-white font-medium">{data.selectedTemplate?.name}</span>
                      <span className="text-white/55 text-sm">{data.selectedTemplate?.description}</span>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {data.selectedTemplate?.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="bg-white/5 border border-white/10 text-white/70 px-2 py-1 rounded-xl text-xs"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </SpotlightCard>

      {/* Download Instructions - Only show after generation */}
      {generatedFile && (
        <DownloadInstructions 
          downloadUrl={generatedFile.downloadUrl} 
          filename={generatedFile.filename} 
        />
      )}
      
      {/* Generate Button */}
      <div className="flex flex-col items-center space-y-4">
        <Button
          onClick={handleGenerate}
          disabled={!data.country || !data.personalInfo.firstName || !data.selectedTemplate}
          loading={loading}
          iconName="ArrowRight"
          iconPosition="right"
          size="xl"
        >
          {loading ? 'Generating Your Resume...' : 'Generate & Download Resume'}
        </Button>

        {(!data.country || !data.personalInfo.firstName || !data.selectedTemplate) && (
          <p className="text-amber-200 text-sm text-center">
            Please ensure you have selected a country, provided your name, and chosen a template to generate the resume.
          </p>
        )}
      </div>
    </div>
  );
};


//! Main ResumeBuilder Component
const ResumeBuilder = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPath, setSelectedPath] = useState(null);
  const [resumeData, setResumeData] = useState({
    objectif:'',
    country: '',
    personalInfo: { firstName: '',lastName:'', email: '', phone: '', address: '' },
    workExperience: [],
    education: [],
    skills: [],
    optional: {},
    selectedTemplate: null,
    hasExtractedResume: false
  });
  const [isNavigating, setIsNavigating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [steps, setSteps] = useState([
    { id: 'choice', label: 'Choose Path', icon: 'FileText' }
  ]);
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    variant: "default",
    title: "",
    description: ""
  });
  
  const [showConfetti,setShowConfetti]=useState(false);

  
  useEffect(() => {
    if (!localStorage.getItem("tour_resume_done")) {
      localStorage.setItem("tour_done", "true");
      resumeBuilderTour();
      localStorage.setItem("tour_resume_done", "true");
    }
    // resumeBuilderTour();
    
  }, []);
  
  // Update steps whenever selectedPath changes
  useEffect(() => {
    if (selectedPath === 'upload') {
      setSteps([
        { id: 'choice', label: 'Choose Path', icon: 'FileText' },
        { id: 'upload', label: 'Upload Resume', icon: 'Upload' },
        { id: 'country', label: 'Select Country', icon: 'Globe'},
        { id: 'additional', label: 'Additional Info', icon: 'Plus' },
        { id: 'template', label: 'Choose Template', icon: 'FileText' },
        { id: 'review', label: 'Review & Generate', icon: 'CheckCircle' }
      ]);
    } else if (selectedPath === 'build') {
      setSteps([
        { id: 'choice', label: 'Choose Path', icon: 'FileText' },
        { id: 'form', label: 'Build Form', icon: 'FileText'},
        { id: 'country', label: 'Select Country', icon: 'Globe' },
        { id: 'adjust', label: 'Adjust Form', icon: 'Settings' },
        { id: 'template', label: 'Choose Template', icon: 'FileText' },
        { id: 'review', label: 'Review & Generate', icon: 'CheckCircle' }
      ]);
    } else if (selectedPath === null) {
      // Reset to just the choice step when no path is selected
      setSteps([
        { id: 'choice', label: 'Choose Path', icon: 'FileText' }
      ]);
    }
  }, [selectedPath]);

  // Handle step navigation when path is selected - FIXED
  useEffect(() => {
    if (selectedPath && currentStep === 0 && !isNavigating && steps.length > 1) {
      setIsNavigating(true);
      setCurrentStep(1);
      setTimeout(() => setIsNavigating(false), 200);
    }
  }, [selectedPath, steps.length]); // Added steps.length as dependency

  // Validation for each step
  const isStepValid = () => {
    if (!steps.length) return false;
    const stepId = steps[currentStep]?.id;
    switch (stepId) {
      case 'choice':
        return !!selectedPath;
      case 'upload':
        return (
          !!resumeData.hasExtractedResume ||
          !!resumeData.personalInfo.firstName ||
          !!resumeData.personalInfo.lastName ||
          !!resumeData.personalInfo.email ||
          !!resumeData.personalInfo.phone ||
          resumeData.workExperience.length > 0 ||
          resumeData.education.length > 0 ||
          resumeData.skills.length > 0
        );
      case 'form':
        return !!resumeData.personalInfo.firstName && 
               !!resumeData.personalInfo.email && 
               !!resumeData.personalInfo.phone && 
               resumeData.workExperience.length > 0 && 
               resumeData.education.length > 0;
      case 'country':
        return !!resumeData.country;
      case 'additional':
      case 'adjust':
        {
          const countryObj = countries.find(c => c.value === resumeData.country);
          const countryLabel = countryObj?.label || resumeData.country;
          return (requirements[countryLabel] || []).every(field => !!resumeData.optional[field]);
        }
      case 'template':
        return !!resumeData.selectedTemplate;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  // Handle step click (allow navigation to previous steps)
  const handleStepClick = (index) => {
    if (index < currentStep && !isNavigating) {
      setIsNavigating(true);
      setCurrentStep(index);
      if (index === 0) {
        setSelectedPath(null);
      }
      setTimeout(() => setIsNavigating(false), 200);
    }
  };

  // Handle Previous button
  const handlePrevious = () => {
    if (currentStep > 0 && !isNavigating) {
      setIsNavigating(true);
      setCurrentStep(prev => {
        const newStep = prev - 1;
        if (newStep === 0) {
          setSelectedPath(null);
        }
        return newStep;
      });
      setTimeout(() => setIsNavigating(false), 200);
    }
  };

  // Handle Next button
  const handleNext = () => {
    if (isStepValid() && currentStep < steps.length - 1 && !isNavigating) {
      setIsNavigating(true);
      setCurrentStep(prev => prev + 1);
      setTimeout(() => setIsNavigating(false), 200);
    } else if (!isStepValid()) {
      alert('Please complete the required fields to proceed.');
    }
  };
  
  // 5. ADD TEMPLATE SELECTION HANDLER
  const handleTemplateSelect = (template) => {
    setResumeData(prev => ({
      ...prev,
      selectedTemplate: template
    }));
  };

  const renderStepContent = () => {
    const stepId = steps[currentStep]?.id;
    switch (stepId) {
      case 'choice':
        return (
          <div className="space-y-8">
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center space-x-3 mb-1">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">Global Resume Formatter</h2>
              </div>
              <p className="text-muted-foreground text-base md:text-lg">Create a professional resume tailored to international standards</p>
            </div>
            <CustomRadioGroup
              selectedValue={selectedPath}
              onChange={setSelectedPath}
              options={[
                { 
                  value: 'upload', 
                  label: 'Upload Existing Resume',
                  description: 'Upload your current resume and we\'ll reformat it for your target country'
                },
                { 
                  value: 'build', 
                  label: 'Build from Scratch',
                  description: 'Create a new resume step by step with guided assistance'
                }
              ]}
            />
          </div>
        );
      case 'upload':
        return <UploadResume onUpload={setResumeData} data={resumeData} setAlertInfo={setAlertInfo} alertInfo={alertInfo}/>;
      case 'form':
        return <FormBuilder data={resumeData} onChange={setResumeData} setAlertInfo={setAlertInfo} />;
      case 'country':
        return <CountrySelection 
          selectedCountry={resumeData.country} 
          onSelect={(country) =>setResumeData(prev => ({ ...prev, country:country }))}
        />;
      case 'additional':
        return <AdditionalFields 
          country={resumeData.country} 
          onChange={setResumeData}
          data={resumeData}
        />;
      case 'adjust':
        return <SmartAdjustment 
          country={resumeData.country} 
          onChange={setResumeData}
          data={resumeData}
        />;
      case 'template': 
        return <TemplateSelection
          selectedTemplate={resumeData.selectedTemplate}
          onSelect={handleTemplateSelect}
          setAlertInfo={setAlertInfo}
        />;
      case 'review':
        return <ReviewGenerate data={resumeData} setAlertInfo={setAlertInfo} alertInfo={alertInfo} setShowConfetti={setShowConfetti}/>;
      default:
        return null;
    }
  };

  const getTipContent = () => {
    const stepId = steps[currentStep]?.id;
    switch (stepId) {
      case 'choice':
        return (
          <>
            <p>• Upload for quick reformatting of existing resumes</p>
            <p>• Build from scratch for complete customization</p>
            <p>• Both paths lead to country-optimized resumes</p>
          </>
        );
      case 'upload':
        return (
          <>
            <p>• Support for PDF and DOCX files only</p>
            <p>• System will automatically extract your information</p>
            <p>• You can review and edit extracted data later</p>
          </>
        );
      case 'form':
        return (
          <>
            <p>• Fill out all required fields marked with *</p>
            <p>• Add multiple work experiences and education entries</p>
            <p>• Use professional email addresses</p>
          </>
        );
      case 'country':
        return (
          <>
            <p>• Choose the country where you'll be applying</p>
            <p>• Different countries have different resume standards</p>
            <p>• This affects required fields and formatting</p>
          </>
        );
      case 'additional':
        return (
          <>
            <p>• Some countries require specific information</p>
            <p>• Complete all highlighted required fields</p>
            <p>• Skip this step if no requirements are shown</p>
          </>
        );
      case 'adjust':
        return (
          <>
            <p>• Review country-specific adjustments</p>
            <p>• Some fields may be hidden for cultural appropriateness</p>
            <p>• Complete any remaining required fields</p>
          </>
        );
      case 'template': 
        return (
          <>
            <p>• Choose a design that matches your industry</p>
            <p>• Preview templates before selecting</p>
            <p>• Different styles work better for different roles</p>
          </>
        );
      case 'review':
        return (
          <>
            <p>• Review all your information carefully</p>
            <p>• Check completion percentage before generating</p>
            <p>• Download will start automatically after generation</p>
          </>
        );
      default:
        return <p>• Follow the steps to create your perfect resume</p>;
    }
  };

  return (
    <AppBackground className="min-h-screen bg-[#0B0B12] text-white relative overflow-hidden">
      {/* <Header /> */}
      
      <main className="pt-12 pb-24 px-4 md:px-6 lg:px-8 relative z-10">
        
        {showConfetti && <Confetti width={1500} height={800} />}
        
        {alertInfo.show && (
          <div style={{zIndex:999999999}} className="fixed top-20 right-4 z-50 max-w-md">
            <Alert variant={alertInfo.variant}>
              <AlertTitle>{alertInfo.title}</AlertTitle>
              <AlertDescription>{alertInfo.description}</AlertDescription>
            </Alert>
          </div>
        )}
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Resume Builder</h1>
            </div>
            <p className="text-muted-foreground text-base">
              Build a country-optimized resume with smart guidance and beautiful templates.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <StepProgressIndicator
              currentStep={currentStep}
              totalSteps={steps?.length}
              steps={steps}
              onStepClick={handleStepClick}
              isNavigating={isNavigating}
            />
          </div>

          {/* Main Content */}
          <SpotlightCard
            className="custom-spotlight-card rounded-3xl overflow-hidden border border-white/10"
            spotlightColor="rgba(99, 102, 241, 0.22)"
          >
            <div className="relative bg-white/5 p-6 md:p-8">
              <div className="absolute inset-0 opacity-25 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-black/0 via-black/25 to-black/55" />
              </div>
              <div className="relative">
              {renderStepContent()}
              </div>
            </div>
          </SpotlightCard>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                disabled={isNavigating}
                style={{zIndex:999999}}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl border border-white/10 bg-white/5 text-white/85 hover:text-white hover:bg-white/10 transition disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                <span>Previous</span>
              </button>
            )}
            
            {currentStep < steps.length - 1 && (
              <button
                onClick={handleNext}
                disabled={!isStepValid()  || isNavigating}
                style={{zIndex:999999}}
                className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold hover:from-[#5B55FF] hover:to-[#7F72FF] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <span>{currentStep === steps.length - 2 ? 'Review & Generate' : 'Continue'}</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Quick Tips Sidebar */}
          <div className="hidden xl:block fixed right-8 bottom-8 w-80 z-20">
            <div className="relative border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
              <div className="absolute inset-0 pointer-events-none">
                <img
                  src={"/assets/images/Gemini_Generated_6_dlmovh.png"}
                  alt=""
                  className="w-full h-full object-cover opacity-55"
                />
                <div className="absolute inset-0 bg-black/55 backdrop-blur-md" />
              </div>
              <div className="relative flex items-center justify-between p-4 border-b border-white/10 bg-white/5">
                <h3 className="font-semibold text-white flex items-center space-x-2">
                  <span>💡</span>
                  <span>Quick Tips</span>
                </h3>
                <button
                  onClick={() => setCollapsed(!collapsed)}
                  className="p-2 rounded-2xl hover:bg-white/10 transition-colors"
                >
                  <ChevronDown
                    className={`w-5 h-5 text-white transition-transform duration-300 ${
                      collapsed ? "-rotate-180" : "rotate-0"
                    }`}
                  />
                </button>
              </div>
              {!collapsed && (
                <div className="relative p-6 space-y-4">
                  <div className="space-y-3 text-sm text-white/70">
                    {getTipContent()}
                  </div>
                  
                  {/* Step indicator in tips */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="flex items-center justify-between text-xs text-white/60">
                      <span>Step {currentStep + 1} of {steps.length}</span>
                      <span className="text-white font-medium">
                        {steps[currentStep]?.label}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Tips */}
          <div className="xl:hidden mt-8">
            <div className="relative border border-white/10 rounded-2xl overflow-hidden">
              <div className="absolute inset-0 pointer-events-none">
                <img
                  src={"/assets/images/Gemini_Generated_6_dlmovh.png"}
                  alt=""
                  className="w-full h-full object-cover opacity-55"
                />
                <div className="absolute inset-0 bg-black/55 backdrop-blur-md" />
              </div>
              <div className="relative p-4">
                <h3 className="font-semibold text-white flex items-center space-x-2 mb-3">
                <span>💡</span>
                <span>Quick Tips</span>
              </h3>
              <div className="space-y-2 text-sm text-white/70">
                {getTipContent()}
              </div>
              </div>
            </div>
          </div>

          {/* Validation Messages */}
          {!isStepValid() && currentStep > 0 && (
            <div className="mt-6 bg-amber-500/10 border border-amber-400/20 rounded-2xl p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-amber-300 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-amber-200">Incomplete Step</h4>
                  <p className="text-sm text-white/70 mt-1">
                    {steps[currentStep]?.id === 'form' && 
                      "Please fill in all required personal information, add at least one work experience and education entry."}
                    {steps[currentStep]?.id === 'country' && 
                      "Please select a target country to continue."}
                    {(steps[currentStep]?.id === 'additional' || steps[currentStep]?.id === 'adjust') && 
                      "Please complete all required fields for your selected country."}
                    {steps[currentStep]?.id === 'template' && 
                      "Please select a resume template to continue."}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Data Persistence Indicator */}
          <div className="mt-6 text-center">
            <p className="text-xs text-white/50">
              Your progress is automatically saved as you work
            </p>
          </div>
        </div>
      </main>
    </AppBackground>
  );
};

export default ResumeBuilder;
